import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Text, Button, Input, Textarea, Switch, FormControl, FormLabel, FormHelperText, Skeleton, Alert, AlertIcon, Wrap, WrapItem,
    NumberInput, NumberInputField, Select, Drawer, DrawerOverlay, DrawerContent, DrawerHeader, DrawerBody, DrawerFooter, DrawerCloseButton,
    useDisclosure, useToast
} from '@chakra-ui/react';
import { Email, WhatsApp, ChevronRight } from '@mui/icons-material';
import useIsPhone from '../../hooks/useIsPhone';

import { Segmented } from '../../components/admin/ui';
import AdminLayout from '../../components/admin/AdminLayout';
import { getCommunications, saveTemplate, resetTemplate, testTemplate, getMessageLog, previewEmail } from '../../services/CommunicationsServices';
import { formatDateTime, shortId } from '../../utils/format';

const CHANNELS = [
    { key: 'email', label: 'Email', icon: Email },
    { key: 'whatsapp', label: 'WhatsApp', icon: WhatsApp }
];

const fill = (text, vars) => String(text || '').replace(/\{\{\s*(\w+)\s*\}\}/g, (m, name) => (name in vars ? vars[name] : m));

// WhatsApp formatting in the preview: *bold*, _italic_
const WhatsappText = ({ text }) => (
    <>{String(text).split('\n').map((line, i) => (
        <React.Fragment key={i}>
            {line.split(/(\*[^*\n]+\*|_[^_\n]+_)/g).map((part, j) =>
                /^\*.+\*$/.test(part) ? <b key={j}>{part.slice(1, -1)}</b>
                    : /^_.+_$/.test(part) ? <i key={j}>{part.slice(1, -1)}</i>
                        : <React.Fragment key={j}>{part}</React.Fragment>)}
            <br />
        </React.Fragment>
    ))}</>
);

const Communications = () => {

    const navigate = useNavigate();
    const editor = useDisclosure();
    const [data, setData] = useState(null);
    const [channel, setChannel] = useState('email');
    const [editing, setEditing] = useState(null); // event key
    const [logs, setLogs] = useState([]);

    const load = () => {
        getCommunications().then(setData).catch(() => setData({ error: true }));
        getMessageLog({ limit: 20 }).then(setLogs).catch(() => { });
    };
    useEffect(load, []);

    const templates = data?.templates?.[channel];
    const status = data?.channels?.[channel];

    const onToggle = async (event, enabled) => {
        const t = templates[event];
        setData(d => ({ ...d, templates: { ...d.templates, [channel]: { ...d.templates[channel], [event]: { ...t, enabled } } } }));
        await saveTemplate(channel, event, { ...t, enabled }).catch(() => load());
    };

    return (
        <AdminLayout title='Communications' subtitle='Messages customers get about their orders' >
            <Box mb='16px' >
                <Segmented label='Channel' value={channel} onChange={setChannel} options={CHANNELS.map(({ key, label }) => ({ value: key, label }))} />
            </Box>

            {data === null ? Array(3).fill(null).map((_, i) => <Skeleton key={i} height='84px' borderRadius='12px' mb={2} />)
                : data.error ? <Alert status='error' borderRadius='8px' ><AlertIcon />Could not load templates.</Alert>
                    : <>
                        {status && !status.connected
                            ? <Alert status='warning' borderRadius='8px' mb={3} fontSize={13} py={2} >
                                <AlertIcon />
                                <Box flex={1} >Emails won't be sent until Resend is connected.</Box>
                                <Button size='xs' variant='outline' bg='raano.white' onClick={() => navigate('/admin/integrations')} >Connect</Button>
                            </Alert>
                            : <Text fontSize={13} color='blackAlpha.600' mb={3} >
                                {channel === 'email' ? 'Sent automatically via Resend.' : 'Open any order and tap WhatsApp to send these from your phone.'}
                            </Text>}

                        <Box bg='raano.white' borderRadius='12px' border='1px solid' borderColor='raano.border' overflow='hidden' mb={6} >
                            {Object.entries(data.events).map(([event, info], i) => {
                                const t = templates[event];
                                return (
                                    <Box key={event} display='flex' alignItems='center' gap={3} px={4} py={3.5} borderTop={i ? '1px solid' : 'none'} borderColor='raano.border' >
                                        <Box as='button' flex={1} minWidth={0} textAlign='left' onClick={() => { setEditing(event); editor.onOpen(); }} >
                                            <Text fontWeight={700} >{info.label}{t.customised && <Text as='span' fontSize={11} fontWeight={600} color='raano.black' ml={2} >Edited</Text>}</Text>
                                            <Text fontSize={12} color='blackAlpha.600' >{info.trigger}{info.hasDelay ? ` (${t.delayDays} ${t.delayDays === 1 ? 'day' : 'days'} after)` : ''}</Text>
                                            <Text fontSize={13} color='blackAlpha.700' mt={1} noOfLines={1} >{channel === 'email' ? t.subject : t.body.split('\n')[0]}</Text>
                                        </Box>
                                        <Switch isChecked={t.enabled} onChange={(e) => onToggle(event, e.target.checked)} aria-label={`Turn ${info.label} on or off`} />
                                        <Box as='button' color='blackAlpha.400' onClick={() => { setEditing(event); editor.onOpen(); }} aria-label='Edit' ><ChevronRight /></Box>
                                    </Box>
                                );
                            })}
                        </Box>

                        <Text fontWeight={700} mb={2} >Recent messages</Text>
                        <Box bg='raano.white' borderRadius='12px' border='1px solid' borderColor='raano.border' overflow='hidden' >
                            {logs.length === 0
                                ? <Text fontSize={14} color='blackAlpha.600' p={4} >Nothing sent yet.</Text>
                                : logs.map((log, i) => (
                                    <Box key={log._id} display='flex' justifyContent='space-between' gap={3} px={4} py={2.5} borderTop={i ? '1px solid' : 'none'} borderColor='raano.border' >
                                        <Box minWidth={0} >
                                            <Text fontSize={13} fontWeight={600} noOfLines={1} >{data.events[log.event]?.label || log.event} · {log.channel === 'email' ? 'Email' : 'WhatsApp'}</Text>
                                            <Text fontSize={12} color='blackAlpha.600' noOfLines={1} >{log.order ? `${shortId(log.order)} · ` : ''}{log.to || 'No recipient'}{log.detail ? ` · ${log.detail}` : ''}</Text>
                                        </Box>
                                        <Box textAlign='right' flexShrink={0} >
                                            <Text fontSize={12} fontWeight={700} color={{ sent: 'green.700', failed: 'red.700', skipped: 'orange.700', opened: 'blue.700' }[log.status]} >
                                                {{ sent: '✓ Sent', failed: '✕ Failed', skipped: '– Skipped', opened: '↗ Opened' }[log.status]}
                                            </Text>
                                            <Text fontSize={11} color='blackAlpha.500' >{formatDateTime(log.createdAt)}</Text>
                                        </Box>
                                    </Box>
                                ))}
                        </Box>
                    </>}

            {data?.templates && editing &&
                <TemplateEditor
                    isOpen={editor.isOpen}
                    onClose={editor.onClose}
                    channel={channel}
                    event={editing}
                    info={data.events[editing]}
                    template={data.templates[channel][editing]}
                    variables={data.variables}
                    onSaved={(t) => setData(d => ({ ...d, templates: { ...d.templates, [channel]: { ...d.templates[channel], [editing]: t } } }))}
                />}
        </AdminLayout>
    )
}

const TemplateEditor = ({ isOpen, onClose, channel, event, info, template, variables, onSaved }) => {

    const toast = useToast();
    const placement = useIsPhone() ? 'bottom' : 'right';
    const bodyRef = useRef(null);
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');
    const [delayDays, setDelayDays] = useState(2);
    const [testTo, setTestTo] = useState('');
    const [busy, setBusy] = useState(null);
    const [lastField, setLastField] = useState('body');

    useEffect(() => {
        if (!isOpen) return;
        setSubject(template.subject || '');
        setBody(template.body || '');
        setDelayDays(template.delayDays ?? 2);
    }, [isOpen, template]);

    const sample = useMemo(() => Object.fromEntries(variables.map(v => [v.name, v.sample])), [variables]);
    const [previewStatus, setPreviewStatus] = useState('onWay');
    const [previewHtml, setPreviewHtml] = useState('');

    // Real branded email preview from the server (debounced while typing)
    useEffect(() => {
        if (!isOpen || channel !== 'email') return;
        const t = setTimeout(() => {
            previewEmail(event, { subject, body, status: previewStatus }).then(r => setPreviewHtml(r.html)).catch(() => { });
        }, 350);
        return () => clearTimeout(t);
    }, [isOpen, channel, event, subject, body, previewStatus]);
    const unknown = useMemo(() => [...`${subject} ${body}`.matchAll(/\{\{\s*(\w+)\s*\}\}/g)].map(m => m[1]).filter(n => !(n in sample)), [subject, body, sample]);

    const insert = (name) => {
        const token = `{{${name}}}`;
        if (lastField === 'subject' && channel === 'email') { setSubject(s => `${s}${token}`); return; }
        const el = bodyRef.current;
        const start = el?.selectionStart ?? body.length, end = el?.selectionEnd ?? body.length;
        const next = body.slice(0, start) + token + body.slice(end);
        setBody(next);
        requestAnimationFrame(() => { el?.focus(); el?.setSelectionRange(start + token.length, start + token.length); });
    };

    const run = async (name, fn) => { setBusy(name); try { await fn(); } finally { setBusy(null); } };

    const onSave = () => run('save', async () => {
        try {
            const saved = await saveTemplate(channel, event, { subject, body, enabled: template.enabled, delayDays });
            onSaved(saved);
            toast({ title: 'Template saved', status: 'success', duration: 1500, position: 'top' });
            onClose();
        } catch (e) {
            toast({ title: 'Could not save', description: e.response?.data?.error, status: 'error', duration: 3000 });
        }
    });

    const onReset = () => run('reset', async () => {
        const fresh = await resetTemplate(channel, event);
        onSaved(fresh);
        setSubject(fresh.subject || ''); setBody(fresh.body); setDelayDays(fresh.delayDays ?? 2);
        toast({ title: 'Back to the default text', status: 'info', duration: 1500, position: 'top' });
    });

    const onTest = () => run('test', async () => {
        if (channel === 'whatsapp') {
            const win = window.open('', '_blank');
            try {
                const result = await testTemplate(channel, event, { body, to: testTo });
                if (win) win.location.href = result.link; else window.location.href = result.link;
            } catch (e) { win?.close(); }
            return;
        }
        try {
            const result = await testTemplate(channel, event, { subject, body, to: testTo, status: previewStatus });
            toast({ title: result.ok ? 'Test sent' : 'Test not sent', description: result.message, status: result.ok ? 'success' : 'warning', duration: 3500, position: 'top' });
        } catch (e) {
            toast({ title: 'Test failed', description: e.response?.data?.error, status: 'error', duration: 3000 });
        }
    });

    return (
        <Drawer isOpen={isOpen} placement={placement || 'bottom'} onClose={onClose} size='lg' >
            <DrawerOverlay />
            <DrawerContent borderTopRadius={{ base: 16, md: 0 }} maxHeight={{ base: '94vh', md: '100vh' }} >
                <DrawerCloseButton />
                <DrawerHeader borderBottomWidth='1px' pr={12} >
                    <Text fontSize={18} >{info.label}</Text>
                    <Text fontSize={12} fontWeight={500} color='blackAlpha.600' >{channel === 'email' ? 'Email' : 'WhatsApp'} · {info.trigger}</Text>
                </DrawerHeader>
                <DrawerBody py={4} >
                    {channel === 'email' &&
                        <FormControl mb={3} >
                            <FormLabel fontSize={14} mb={1} >Subject</FormLabel>
                            <Input value={subject} onFocus={() => setLastField('subject')} onChange={(e) => setSubject(e.target.value)} />
                        </FormControl>}

                    <FormControl mb={2} >
                        <FormLabel fontSize={14} mb={1} >Message</FormLabel>
                        <Textarea ref={bodyRef} rows={10} fontSize={14} fontFamily={channel === 'whatsapp' ? 'inherit' : 'inherit'} value={body} onFocus={() => setLastField('body')} onChange={(e) => setBody(e.target.value)} />
                        {channel === 'whatsapp' && <FormHelperText fontSize={12} >*bold* and _italic_ work in WhatsApp.</FormHelperText>}
                    </FormControl>

                    <Text fontSize={12} fontWeight={700} color='blackAlpha.600' mb={1} >Tap to insert</Text>
                    <Wrap spacing={1.5} mb={3} >
                        {variables.map(v => (
                            <WrapItem key={v.name} >
                                <Button size='xs' variant='wipe' height='40px' px='18px' fontSize='14px' title={v.label} onClick={() => insert(v.name)} >{v.label}</Button>
                            </WrapItem>
                        ))}
                    </Wrap>
                    {unknown.length > 0 &&
                        <Alert status='warning' borderRadius={8} fontSize={12} py={1.5} mb={3} ><AlertIcon />Unknown: {unknown.map(n => `{{${n}}}`).join(', ')}. These will show as typed.</Alert>}

                    {info.hasDelay &&
                        <FormControl mb={4} >
                            <FormLabel fontSize={14} mb={1} >Send how many days after delivery?</FormLabel>
                            <NumberInput min={0} max={30} value={delayDays} onChange={(_, n) => setDelayDays(Number.isNaN(n) ? 0 : n)} maxWidth='120px' >
                                <NumberInputField />
                            </NumberInput>
                        </FormControl>}

                    <Text fontSize={12} fontWeight={700} color='blackAlpha.600' mb={2} >Preview with sample order</Text>
                    {channel === 'email'
                        ? <Box mb={4} >
                            {event === 'order_status' &&
                                <Select size='sm' mb={2} maxWidth='220px' value={previewStatus} onChange={(e) => setPreviewStatus(e.target.value)} aria-label='Preview status' >
                                    <option value='onWay'>Preview: Shipped</option>
                                    <option value='delivered'>Preview: Delivered</option>
                                    <option value='cancelled'>Preview: Cancelled</option>
                                </Select>}
                            <Box border='1px solid' borderColor='blackAlpha.200' borderRadius={12} overflow='hidden' >
                                <Box px={3} py={2} bg='raano.surface' borderBottom='1px solid' borderColor='raano.border' >
                                    <Text fontSize={11} color='blackAlpha.600' >Subject</Text>
                                    <Text fontSize={13} fontWeight={700} noOfLines={2} >{fill(subject, sample)}</Text>
                                </Box>
                                <iframe title='Email preview' srcDoc={previewHtml} style={{ width: '100%', height: '560px', border: 0, display: 'block', background: '#f3f4f7' }} />
                            </Box>
                            <Text fontSize={11} color='blackAlpha.500' mt={1} >Your message appears in the middle; order details, tracker and buttons are added automatically. Product photos show once your store is on a public web address.</Text>
                        </Box>
                        : <Box bg='#e5ddd5' borderRadius={12} p={3} mb={4} >
                            <Box bg='#dcf8c6' borderRadius='10px 10px 2px 10px' px={3} py={2} ml='auto' maxWidth='88%' fontSize={14} lineHeight={1.5} boxShadow='sm' >
                                <WhatsappText text={fill(body, sample)} />
                            </Box>
                        </Box>}

                    <FormControl mb={2} >
                        <FormLabel fontSize={14} mb={1} >Send a test to</FormLabel>
                        <Box display='flex' gap={2} >
                            <Input placeholder={channel === 'email' ? 'you@example.com' : 'Your WhatsApp number'} type={channel === 'email' ? 'email' : 'tel'} value={testTo} onChange={(e) => setTestTo(e.target.value)} />
                            <Button flexShrink={0} variant='wipe' height='40px' px='18px' fontSize='14px' isLoading={busy === 'test'} isDisabled={!testTo.trim()} onClick={onTest} >Send test</Button>
                        </Box>
                    </FormControl>

                    <Button size='sm' variant='ghost' mt={2} isLoading={busy === 'reset'} onClick={onReset} >Reset to default text</Button>
                </DrawerBody>
                <DrawerFooter borderTopWidth='1px' gap={3} >
                    <Button flex={1} variant='wipe' height='40px' px='18px' fontSize='14px' onClick={onClose} >Cancel</Button>
                    <Button flex={2} variant='ink' height='40px' px='18px' fontSize='14px' isLoading={busy === 'save'} onClick={onSave} >Save template</Button>
                </DrawerFooter>
            </DrawerContent>
        </Drawer>
    )
}

export default Communications;
